import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/AuthProvider';
import { getFirestore, getDoc, doc } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import app from '../../../Firebase';
import { getLocationData, convertStoreAddressToServiceAddress } from '../../../service/LocationService';
import LocationPicker from './LocationPicker';
import './AddStoreWorkflow.css';

// LocalStorage keys for caching
const LOCATION_CONFIG_CACHE_KEY = 'dineplatform_location_config_cache';
const LOCATION_CONFIG_TIMESTAMP_KEY = 'dineplatform_location_config_timestamp';

// Cache duration: 30 minutes in milliseconds
const CACHE_DURATION = 30 * 60 * 1000;

interface Store {
  storeId?: string;
  name: string;
  description: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  timezone: string;
  currency: string;
  stripeTestConnected: boolean;
  stripeLiveConnected: boolean;
  stripeConnected: boolean;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
  location?: {
    latitude: number;
    longitude: number;
  };
  menuData?: any;
}

interface OrganizationConfig {
  id: string;
  orgId: string;
  orgName: string;
  stripeAccount: string;
  country: string;
  countryCode: string;
  currencyCode: string;
  currencySymbol: string;
  region: string;
  availableTimezones: string[];
}

interface AddStoreWorkflowProps {
  functions: Functions;
  onSubmit: (storeData: Omit<Store, 'appId' | 'orgId' | 'stripeAccount'>) => void;
  onCancel: () => void;
  appRegion: string;
  organizationId: string;
  appId: string;
  appName: string;
  appType: string;
}

type WorkflowStep = 'details' | 'location' | 'menu' | 'complete';

// Utility functions for cache management
let inMemoryCache: { data: Record<string, OrganizationConfig> | null; timestamp: number } = { data: null, timestamp: 0 };

const getCachedLocationConfig = (): Record<string, OrganizationConfig> | null => {
  try {
    // Check in-memory cache first (for same session)
    if (inMemoryCache.data && (Date.now() - inMemoryCache.timestamp < CACHE_DURATION)) {
      console.log('Using in-memory cache for AddStoreWorkflow');
      return inMemoryCache.data;
    }

    // Check localStorage cache
    const cachedData = localStorage.getItem(LOCATION_CONFIG_CACHE_KEY);
    const timestamp = localStorage.getItem(LOCATION_CONFIG_TIMESTAMP_KEY);
    
    if (!cachedData || !timestamp) {
      return null;
    }
    
    const cacheTime = parseInt(timestamp, 10);
    const now = Date.now();
    
    // Check if cache is still valid (within 30 minutes)
    if (now - cacheTime > CACHE_DURATION) {
      // Cache expired, remove it
      localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
      localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
      inMemoryCache = { data: null, timestamp: 0 };
      return null;
    }
    
    const parsedData = JSON.parse(cachedData);
    // Update in-memory cache
    inMemoryCache = { data: parsedData, timestamp: cacheTime };
    
    return parsedData;
  } catch (error) {
    console.error('Error reading location config cache:', error);
    // Clear corrupted cache
    localStorage.removeItem(LOCATION_CONFIG_CACHE_KEY);
    localStorage.removeItem(LOCATION_CONFIG_TIMESTAMP_KEY);
    inMemoryCache = { data: null, timestamp: 0 };
    return null;
  }
};

const setCachedLocationConfig = (data: Record<string, OrganizationConfig>): void => {
  try {
    const timestamp = Date.now();
    localStorage.setItem(LOCATION_CONFIG_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(LOCATION_CONFIG_TIMESTAMP_KEY, timestamp.toString());
    // Update in-memory cache
    inMemoryCache = { data, timestamp };
  } catch (error) {
    console.error('Error caching location config:', error);
  }
};

const AddStoreWorkflow: React.FC<AddStoreWorkflowProps> = ({
  functions,
  onSubmit,
  onCancel,
  appRegion,
  organizationId,
  appId,
  appName,
  appType
}) => {
  const { currentUser } = useAuth();
  
  // State
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('details');
  const [isAppAdmin, setIsAppAdmin] = useState<boolean>(false);
  const [checkingPermission, setCheckingPermission] = useState<boolean>(true);
  const [organizationConfig, setOrganizationConfig] = useState<OrganizationConfig | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string>('');
  const [isProcessingMenu, setIsProcessingMenu] = useState(false);
  const [menuProcessingError, setMenuProcessingError] = useState<string>('');
  const [isLoadingOrganizationConfig, setIsLoadingOrganizationConfig] = useState(true);
  const [isLoadingConfigInProgress, setIsLoadingConfigInProgress] = useState(false);

  const [newMenuName, setNewMenuName] = useState<string>('');
  
  // Form data
  const [formData, setFormData] = useState<Store>({
    name: appType === "single-store" ? appName : "",
    description: '', // Keep for backend compatibility but won't show in UI
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    timezone: '',
    currency: '',
    storeId: '',
    stripeTestConnected: false,
    stripeLiveConnected: false,
    stripeConnected: false,
    isActive: false,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generatedStoreId, setGeneratedStoreId] = useState<string>('');
  // const [customStoreId, setCustomStoreId] = useState<string>('');
  const [isEditingStoreId, setIsEditingStoreId] = useState<boolean>(false);

  // Location state
  const [detectedLocation, setDetectedLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // Menu processing state - Updated to support multiple file types
  const [menuFiles, setMenuFiles] = useState<File[]>([]);
  const [menuNames, setMenuNames] = useState<string[]>(['Main Menu']); // Start with Main Menu by default
  const [processedMenuData, setProcessedMenuData] = useState<any>(null);

  // Load organization config with caching and duplicate request prevention
  useEffect(() => {
    const loadOrganizationConfig = async (forceRefresh: boolean = false) => {
      if (!organizationId || !functions) {
        setIsLoadingOrganizationConfig(false);
        return;
      }

      // Prevent duplicate requests
      if (isLoadingConfigInProgress && !forceRefresh) {
        console.log('Already loading organization config, skipping duplicate request');
        return;
      }

      setIsLoadingConfigInProgress(true);
      setIsLoadingOrganizationConfig(true);

      try {
        // Check cache first unless force refresh is requested
        if (!forceRefresh) {
          const cachedData = getCachedLocationConfig();
          if (cachedData && cachedData[organizationId]) {
            console.log('Loading organization config from cache for AddStoreWorkflow');
            const config = cachedData[organizationId];
            setOrganizationConfig(config);
            
            setFormData(prev => ({
              ...prev,
              currency: config.currencyCode,
              country: config.country,
              timezone: config.availableTimezones[0] || 'UTC'
            }));
            
            setIsLoadingOrganizationConfig(false);
            setIsLoadingConfigInProgress(false);
            return;
          }
        }

        // Fetch from server
        console.log('Fetching organization config from server for AddStoreWorkflow...');
        const getLocationConfig = httpsCallable(functions, 'getLocationConfig');
        const result = await getLocationConfig({});
        const data = result.data as { success: boolean; data: Record<string, OrganizationConfig> };
        
        if (data.success && data.data[organizationId]) {
          // Cache the fresh data
          setCachedLocationConfig(data.data);
          
          const config = data.data[organizationId];
          setOrganizationConfig(config);
          
          setFormData(prev => ({
            ...prev,
            currency: config.currencyCode,
            country: config.country,
            timezone: config.availableTimezones[0] || 'UTC'
          }));
          
          console.log('Organization config loaded from server and cached for AddStoreWorkflow');
        } else {
          console.error('Organization not found in config data');
        }
      } catch (error) {
        console.error('Error loading organization config:', error);
      } finally {
        setIsLoadingOrganizationConfig(false);
        setIsLoadingConfigInProgress(false);
      }
    };

    loadOrganizationConfig();
  }, [organizationId, functions]); // Only depend on organizationId and functions

  // Check admin permission
  useEffect(() => {
    const checkAdminPermission = async () => {
      if (!currentUser?.email) {
        setIsAppAdmin(false);
        setCheckingPermission(false);
        return;
      }
      
      try {
        const db = getFirestore(app);
        const userDocRef = doc(db, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userPermissions = userData?.permissions || [];
          setIsAppAdmin(userPermissions.includes('APP_ADMIN'));
        }
      } catch (error) {
        console.error('Error checking admin permission:', error);
      } finally {
        setCheckingPermission(false);
      }
    };
    
    if (currentUser) {
      checkAdminPermission();
    } else {
      setCheckingPermission(false);
    }
  }, [currentUser]);

  // Generate store ID
  const generateStoreId = (name: string): string => {
    if (!name.trim()) return '';
    
    const baseId = name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const hex = Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    return `${baseId}-${hex}`;
  };

  // Form validation - Updated to require at least one menu type
  const isStepValid = (step: WorkflowStep) => {
    switch (step) {
      case 'details':
        return formData.name.trim() !== '' && 
               formData.address.trim() !== '' && 
               formData.city.trim() !== '' && 
               formData.state.trim() !== '' && 
               formData.zipCode.trim() !== '';
      case 'location':
        return detectedLocation !== null;
      case 'menu':
        return menuFiles.length > 0 && menuNames.length > 0;
      default:
        return true;
    }
  };

  const handleInputChange = (field: keyof Store, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle proceeding to location step
  const handleProceedToLocation = async () => {
    if (!isStepValid('details') || !organizationConfig) {
      return;
    }

    setIsLoadingLocation(true);
    setLocationError('');

    try {
      // Convert form data to address format expected by location service
      const address = convertStoreAddressToServiceAddress(formData);
      
      // Get location data
      const locationData = await getLocationData(address, organizationConfig.countryCode);
      
      if (locationData && locationData.latitude && locationData.longitude) {
        setDetectedLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude
        });
        setCurrentStep('location');
      } else {
        setLocationError('Could not find the location for this address. Please check the address and try again.');
      }
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Error looking up address location. Please try again.');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  // Handle location change from map
  const handleLocationChange = (latitude: number, longitude: number) => {
    setDetectedLocation({ latitude, longitude });
  };

  // Updated to handle multiple file types (images, PDFs, CSV, Excel)
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // Filter for supported file types
      const supportedFiles = Array.from(files).filter((file: File) => {
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        
        return (
          fileType.startsWith('image/') ||
          fileType === 'application/pdf' ||
          fileType === 'text/csv' ||
          fileType === 'application/csv' ||
          fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')
        );
      });
      
      if (supportedFiles.length > 0) {
        setMenuFiles(prev => [...prev, ...supportedFiles]);
      }
      
      // Show warning if some files were rejected
      if (supportedFiles.length !== files.length) {
        alert(`Some files were not added. Only images, PDFs, CSV, and Excel files are supported.`);
      }
    }
  };

  // Remove menu file
  const removeFile = (index: number) => {
    setMenuFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Updated menu name handlers
  const addMenuName = () => {
    if (newMenuName.trim()) {
      setMenuNames(prev => [...prev, newMenuName.trim()]);
      setNewMenuName('');
    }
  };

  const updateMenuName = (index: number, value: string) => {
    setMenuNames(prev => prev.map((name, i) => i === index ? value : name));
  };

  // Updated to allow removing all menu types
  const removeMenuName = (index: number) => {
    setMenuNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMenuName();
    }
  };

// Process menu with Gemini - Updated to send all file types as images
const processMenuWithGemini = async () => {
  if (menuFiles.length === 0 || menuNames.length === 0) {
    return;
  }

  setIsProcessingMenu(true);
  setMenuProcessingError('');

  try {
    const processMenuFunction = httpsCallable(functions, 'processMenuFiles');
    
    const fileData: any = {
      menuNames: menuNames.filter(name => name.trim() !== '')
    };

    // Convert all files to base64 and send as images
    if (menuFiles.length > 0) {
      const filePromises = menuFiles.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      fileData.images = await Promise.all(filePromises);
    }
    
    const result = await processMenuFunction(fileData);

    const data = result.data as { success: boolean; menuData?: any; error?: string };
    
    if (data.success && data.menuData) {
      // Log the Gemini output to console for debugging
      console.log('🍽️ Gemini Menu Processing Result:');
      console.log(JSON.stringify(data.menuData, null, 2));
      
      // Store the processed menu data
      setProcessedMenuData(data.menuData);
      
      // Now create the store with all collected data
      await createStoreWithAllData(data.menuData);
      
    } else {
      setMenuProcessingError(data.error || 'Failed to process menu files');
    }
  } catch (error) {
    console.error('Error processing menu:', error);
    setMenuProcessingError('Error processing menu. Please try again.');
  } finally {
    setIsProcessingMenu(false);
  }
};

  // Create store with all collected data
  const createStoreWithAllData = async (menuData: any) => {
    if (!organizationConfig || !detectedLocation) {
      setMenuProcessingError('Missing required data for store creation');
      return;
    }

    setIsSubmitting(true);

    try {
      const createStoreFunction = httpsCallable(functions, 'createStore');
      
      const storeDataForCreation = {
        ...formData,
        description: `${formData.name} store`,
        location: {
          latitude: detectedLocation.latitude,
          longitude: detectedLocation.longitude
        },
        menuData: menuData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Creating store with data:', {
        hasLocation: true,
        hasMenuData: true,
        menuItemCount: menuData?.menu?.length || 0
      });

      const result = await createStoreFunction({
        storeData: storeDataForCreation,
        appId: appId // Use the appId from props
      });

      const response = result.data as { 
        success: boolean; 
        storeId?: string; 
        message?: string; 
        error?: string 
      };

      if (response.success) {
        console.log('✅ Store created successfully:', response);
        setCurrentStep('complete');
        
        // Show success message
        alert(`Store created successfully! Store ID: ${response.storeId}`);
      } else {
        throw new Error(response.error || 'Failed to create store');
      }

    } catch (error) {
      console.error('Error creating store:', error);
      setMenuProcessingError('Failed to create store. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get file type display name and icon
  const getFileDisplayInfo = (file: File) => {
    const fileType = file.type.toLowerCase();
    const fileName = file.name.toLowerCase();
    
    if (fileType.startsWith('image/')) {
      return { type: 'Image', icon: '🖼️' };
    } else if (fileType === 'application/pdf') {
      return { type: 'PDF', icon: '📄' };
    } else if (fileType === 'text/csv' || fileType === 'application/csv' || fileName.endsWith('.csv')) {
      return { type: 'CSV', icon: '📊' };
    } else if (
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.xlsx')
    ) {
      return { type: 'Excel', icon: '📈' };
    }
    return { type: 'File', icon: '📎' };
  };

  // Render store details form
  const renderDetailsForm = () => {
    return (
      <div className="step-content">
        <div className="step-header">
          <h2>Store Details</h2>
          <p>Enter your store information and address</p>
        </div>
        
        <div className="form-section">
          {/* Store Name */}
          <div className="form-group">
            <label htmlFor="store-name">Store Name *</label>
            <input
              id="store-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter your store name"
              className="form-input"
              autoFocus
            />
          </div>
          
          {/* Address Section */}
          <div className="form-group">
            <label htmlFor="store-address">Street Address *</label>
            <input
              id="store-address"
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder="123 Main Street"
              className="form-input"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="store-city">City *</label>
              <input
                id="store-city"
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="City"
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="store-state">State/Province *</label>
              <input
                id="store-state"
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="State or Province"
                className="form-input"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="store-zip">Zip/Postal Code *</label>
              <input
                id="store-zip"
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                placeholder="12345"
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="store-country">Country *</label>
              <input
                id="store-country"
                type="text"
                value={formData.country}
                readOnly
                className="form-input readonly"
              />
              <small>Based on your organization settings</small>
            </div>
          </div>
          
          {/* Business Settings */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="store-timezone">Timezone *</label>
              <select
                id="store-timezone"
                value={formData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="form-select"
              >
                {organizationConfig?.availableTimezones.map((timezone: string) => (
                  <option key={timezone} value={timezone}>
                    {timezone}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="store-currency">Currency *</label>
              <input
                id="store-currency"
                type="text"
                value={formData.currency}
                readOnly
                className="form-input readonly"
              />
              <small>Based on your organization settings</small>
            </div>
          </div>

          {locationError && (
            <div className="error-message">
              {locationError}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render location verification step
  const renderLocationForm = () => {
    const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`;
    
    return (
      <div className="step-content">
        <div className="step-header">
          <h2>Verify Store Location</h2>
          <p>Confirm your store's exact location on the map below</p>
        </div>
        
        <div className="form-section">
          {detectedLocation && (
            <LocationPicker
              initialLatitude={detectedLocation.latitude}
              initialLongitude={detectedLocation.longitude}
              onLocationChange={handleLocationChange}
              address={fullAddress}
              className="location-picker"
            />
          )}
        </div>
      </div>
    );
  };

// Updated renderMenuForm function with support for multiple file types

const renderMenuForm = () => {

  // Add this function to handle the upload area click
  const handleUploadAreaClick = () => {
    const fileInput = document.getElementById('menu-files');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Add drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Filter for supported file types
      const supportedFiles = Array.from(files).filter((file: File) => {
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        
        return (
          fileType.startsWith('image/') ||
          fileType === 'application/pdf' ||
          fileType === 'text/csv' ||
          fileType === 'application/csv' ||
          fileType === 'application/vnd.ms-excel' ||
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          fileName.endsWith('.csv') ||
          fileName.endsWith('.xls') ||
          fileName.endsWith('.xlsx')
        );
      });
      
      if (supportedFiles.length > 0) {
        setMenuFiles((prev: File[]) => [...prev, ...supportedFiles]);
      }

      // Show warning if some files were rejected
      if (supportedFiles.length !== files.length) {
        alert(`Some files were not added. Only images, PDFs, CSV, and Excel files are supported.`);
      }
    }
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>Upload Menu Files</h2>
        <p>Define your menu types and upload corresponding files for AI processing</p>
      </div>
      
      <div className="form-section">
        {/* Two Column Layout */}
        <div className="menu-form-columns">
          {/* Left Column - Menu Types */}
          <div className="menu-types-column">
            <div className="menu-types-header">
              <h3>Menu Types</h3>
              <p>Define the different menus you want to process</p>
            </div>
            
            <div className="menu-types-list">
              {menuNames.map((name, index) => (
                <div key={index} className="menu-type-item">
                  <div className="menu-type-content">
                    <span className="menu-type-name">{name || 'Unnamed Menu'}</span>
                    <span className="menu-type-index">#{index + 1}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeMenuName(index)}
                    className="menu-type-remove"
                    title="Remove menu type"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="add-menu-section">
              <div className="add-menu-input-group">
                <input
                  type="text"
                  value={newMenuName}
                  onChange={(e) => setNewMenuName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter menu name (e.g., Drinks, Desserts)"
                  className="add-menu-input"
                />
                <button
                  type="button"
                  onClick={addMenuName}
                  disabled={!newMenuName.trim()}
                  className="add-menu-button"
                >
                  Add
                </button>
              </div>
              <small className="add-menu-hint">
                You can have a single menu with everything, or you can have separated menus.
                </small><br />
              <small className="add-menu-hint">
                 e.g. Breakfast Menu, Lunch Menu, Kids Menu, Drinks Menu. 
              </small><br />
              <small className="add-menu-hint" style={{color: "red"}}>
                This is important if you have different timings for different products.
              </small>
            </div>
          </div>

          {/* Right Column - File Upload */}
          <div className="menu-upload-column">
            {/* File Upload Area */}
            <div className="file-upload-container">
              {/* Hidden file input */}
              <input
                id="menu-files"
                type="file"
                accept="image/*,.pdf,.csv,.xls,.xlsx,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              
              {/* Clickable upload area */}
              <div 
                className="upload-area"
                onClick={handleUploadAreaClick}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="upload-icon">📎</div>
                <p className="upload-title">
                  Click to upload menu files
                </p>
                <p className="upload-subtitle">
                  or drag and drop files here
                </p>
                <small className="upload-formats">
                  Supports: Images (JPG, PNG, WebP), PDFs, CSV, Excel files. Multiple files allowed.
                </small>
              </div>
            </div>

            {/* File Preview */}
            {menuFiles.length > 0 && (
              <div className="uploaded-files-section">
                <div className="uploaded-files-header">
                  <span>Uploaded Files ({menuFiles.length})</span>
                </div>
                <div className="file-preview-grid">
                  {menuFiles.map((file, index) => {
                    const { type, icon } = getFileDisplayInfo(file);
                    return (
                      <div key={index} className="file-preview-item">
                        {file.type.startsWith('image/') ? (
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={`Menu ${index + 1}`}
                            className="preview-image"
                          />
                        ) : (
                          <div className="file-icon-preview">
                            <span className="file-icon">{icon}</span>
                            <span className="file-type">{type}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="remove-file-btn"
                          title="Remove file"
                        >
                          ×
                        </button>
                        <span className="file-name">
                          {file.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {menuProcessingError && (
          <div className="error-message">
            {menuProcessingError}
          </div>
        )}
      </div>
    </div>
  );
};

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 'details') {
      handleProceedToLocation();
    } else if (currentStep === 'location') {
      setCurrentStep('menu');
    } else if (currentStep === 'menu') {
      processMenuWithGemini(); // This will now handle the complete flow
    }
  };

  const handleBack = () => {
    if (currentStep === 'menu') {
      setCurrentStep('location');
    } else if (currentStep === 'location') {
      setCurrentStep('details');
      setLocationError('');
    } else {
      onCancel();
    }
  };

  const getCurrentStepTitle = () => {
    switch (currentStep) {
      case 'details': return 'Store Details';
      case 'location': return 'Location Verification';
      case 'menu': return 'Menu Upload';
      case 'complete': return 'Store Created';
      default: return 'Store Setup';
    }
  };

  const getNextButtonText = () => {
    if (currentStep === 'details') {
      return isLoadingLocation ? (
        <>
          <div className="spinner" />
          Finding Location...
        </>
      ) : 'Continue to Location';
    } else if (currentStep === 'location') {
      return 'Continue to Menu';
    } else if (currentStep === 'menu') {
      return (isProcessingMenu || isSubmitting) ? (
        <>
          <div className="spinner" />
          {isProcessingMenu ? 'Processing Menu...' : 'Creating Store...'}
        </>
      ) : 'Process Files & Create Store';
    }
    return 'Next';
  };

  const isNextDisabled = () => {
    if (currentStep === 'details') {
      return !isStepValid('details') || isLoadingLocation;
    } else if (currentStep === 'location') {
      return !isStepValid('location');
    } else if (currentStep === 'menu') {
      return !isStepValid('menu') || isProcessingMenu || isSubmitting;
    }
    return false;
  };

  // Get progress percentage
  const getProgress = () => {
    switch (currentStep) {
      case 'details': return 33;
      case 'location': return 66;
      case 'menu': return 100;
      default: return 0;
    }
  };

  if (checkingPermission || isLoadingOrganizationConfig) {
    return (
      <div className="add-store-workflow">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading store setup...</p>
        </div>
      </div>
    );
  }

  if (!organizationConfig) {
    return (
      <div className="add-store-workflow">
        <div className="error-state">
          <h3>Configuration Error</h3>
          <p>Could not load organization configuration. Please try refreshing the page.</p>
          <button onClick={onCancel} className="nav-button secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-store-workflow">
      {/* Header */}
      <div className="workflow-header">
        <button className="back-button" onClick={handleBack}>
          ← Back to {currentStep === 'details' ? appName : currentStep === 'location' ? 'Store Details' : 'Location Verification'}
        </button>
        <div className="org-info">
          <span>{organizationConfig.orgName}</span>
          <span className="org-region">({organizationConfig.region})</span>
        </div>
      </div>

      {/* Progress Section */}
      <div className="progress-section">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${getProgress()}%` }}
          />
        </div>
        <div className="progress-text">
          Step {currentStep === 'details' ? '1' : currentStep === 'location' ? '2' : '3'} of 3: {getCurrentStepTitle()}
        </div>
      </div>

      {/* Main content */}
      <div className="workflow-content-store">
        {currentStep === 'details' && renderDetailsForm()}
        {currentStep === 'location' && renderLocationForm()}
        {currentStep === 'menu' && renderMenuForm()}
        {currentStep === 'complete' && (
          <div className="step-content">
            <div className="step-header">
              <h2>Store Created Successfully!</h2>
              <p>Your store has been created with location data and menu information.</p>
            </div>
            <div className="form-section">
              <div className="success-summary">
                <div className="summary-item">
                  <strong>Store Name:</strong> {formData.name}
                </div>
                <div className="summary-item">
                  <strong>Location:</strong> {formData.address}, {formData.city}, {formData.state} {formData.zipCode}
                </div>
                <div className="summary-item">
                  <strong>Coordinates:</strong> {detectedLocation?.latitude.toFixed(6)}, {detectedLocation?.longitude.toFixed(6)}
                </div>
                {processedMenuData && (
                  <>
                    <div className="summary-item">
                      <strong>Menu Items:</strong> {processedMenuData.menu?.length || 0} items processed
                    </div>
                    <div className="summary-item">
                      <strong>Menu Categories:</strong> {processedMenuData.menuSettings?.[0]?.categories?.length || 0} categories created
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation - fixed to bottom */}
      <div className="workflow-navigation">
        <button
          type="button"
          className="nav-button secondary"
          onClick={handleBack}
          disabled={isLoadingLocation || isSubmitting || isProcessingMenu}
        >
          {currentStep === 'details' ? 'Cancel' : currentStep === 'complete' ? 'Close' : 'Back'}
        </button>
        
        {currentStep === 'complete' ? (
          <button
            type="button"
            className="nav-button primary"
            onClick={onCancel} // Return to the parent component
          >
            Done
          </button>
        ) : (
          <button
            type="button"
            className="nav-button primary"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            {getNextButtonText()}
          </button>
        )}
      </div>
    </div>
  );
};

export default AddStoreWorkflow;